import authenticatedAxiosInstance from './authenticatedAxiosInstance.service';
import authHeader from './auth-header';

const API_URL = process.env.REACT_APP_API_URL + '/floorplans';

const routes = {
  // TODO: rename to floorplans summary by dealer
  FLOOR_PLANS: `${API_URL}`,
  FLOOR_PLANS_SUMMARY: `${API_URL}/summary`,
  ACTIVE_VEHICLES: `${API_URL}/active-vehicles`,
  ACTIVE_VEHICLE_TITLE: `${API_URL}/active-vehicles/title-doc`,
  ACTIVE_VEHICLE_GOT: `${API_URL}/active-vehicles/got`,
  SCAN_DOCS: `${API_URL}/scan-docs`,
  UPLOAD_SCAN_DOC: `${API_URL}/upload-scan-doc`,
  MTHLY_BILLING: `${API_URL}/mthly-billing`,
  REFRESH_BB: `${API_URL}/refresh-bb`,
  BALANCE_HISTORY: `${API_URL}/balance-history`,
  BILLING_PAYMENT_LOG: `${API_URL}/billing-payment-log`,
  BILLING_PAYMENT_LOG_DETAIL: `${API_URL}/billing-payment-log-detail`,
  ASSIGNABLE_LENDERS: `${API_URL}/assignable-lenders`,
  ASSIGN_LENDER: `${API_URL}/assign-lender`,
  FUNDING_LOGS: `${API_URL}/funding-logs`,
  PAID_OFF_VEHICLES: `${API_URL}/paid-off-vehicles`,
  PAYOFF_REQUESTS: `${API_URL}/payoff-requests`,
  PENDING_FUNDING: `${API_URL}/pending-funding`,
  PAYOFF_VEHICLES: `${API_URL}/payoff-vehicles`,
  PAYOFF_LOANS: `${API_URL}/payoff-loans`,
  APPROVED_VEHICLES: `${API_URL}/approved-vehicles`,
  ACTIVE_LOANS: `${API_URL}/active-loans`,
  APPROVED_LOANS: `${API_URL}/approved-loans`,
  FUNDING_VEHICLES: `${API_URL}/funding-vehicles`,
  CANCEL_VEHICLES: `${API_URL}/cancel-vehicles`,
  VEHICLE: `${API_URL}/vehicle`,
  DECODE_VIN: `${API_URL}/decode-vin`,
  FUNDING_DETAILS: `${API_URL}/funding-details`,
};

class FloorplansService {
  getFloorplansDashboard(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.FLOOR_PLANS, { headers: authHeader(), signal });
  }

  getFloorplan(RecId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.FLOOR_PLANS}/${RecId}`, {
      headers: authHeader(),
      signal,
    });
  }

  updateFloorplan(id: string, data: any) {
    return authenticatedAxiosInstance.put(`${routes.FLOOR_PLANS}/${id}`, data, {
      headers: authHeader(),
    });
  }

  getFloorplansSummaryByDealer(CompId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.FLOOR_PLANS_SUMMARY, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  getMonthlyBilling(CompId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.MTHLY_BILLING, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  monthlyBilling(invoiceDate: string, dueDate: string, compID: number, byUserName?: string) {
    return authenticatedAxiosInstance.post(
      `${routes.MTHLY_BILLING}`,
      { invoiceDate, dueDate, compID, byUserName },
      {
        headers: authHeader(),
      }
    );
  }

  refreshBB(compID: number, byUserName?: string) {
    return authenticatedAxiosInstance.post(
      `${routes.REFRESH_BB}`,
      { compID, byUserName },
      {
        headers: authHeader(),
      }
    );
  }

  async getActiveVehicles(CompId?: string, searchText?: string, signal?: AbortSignal) {
    const result = await authenticatedAxiosInstance.get(routes.ACTIVE_VEHICLES, {
      headers: authHeader(),
      params: { CompId, searchText },
      signal,
    });
    return result;
  }

  getScanDocsByFloorplanId(FloorplanId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.SCAN_DOCS, {
      headers: authHeader(),
      params: { FloorplanId },
      signal,
    });
  }

  uploadScanDoc(
    description: string,
    compId: string,
    floorplanId: string,
    byUserName: string,
    file: File
  ) {
    const formData = new FormData();
    formData.append('description', description);
    formData.append('compId', compId.toString());
    formData.append('floorplanId', floorplanId.toString());
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(routes.UPLOAD_SCAN_DOC, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  updateScanDoc(recId: string, data: any) {
    return authenticatedAxiosInstance.put(`${routes.SCAN_DOCS}/${recId}`, data, {
      headers: authHeader(),
    });
  }

  deleteScanDoc(recId: string) {
    return authenticatedAxiosInstance.delete(`${routes.SCAN_DOCS}`, {
      headers: authHeader(),
      data: { recId },
    });
  }

  setActiveVehicleTitle(transType: number, recId: number, byUserName?: string) {
    return authenticatedAxiosInstance.post(
      `${routes.ACTIVE_VEHICLE_TITLE}`,
      { transType, recId, byUserName },
      {
        headers: authHeader(),
      }
    );
  }

  setActiveVehicleGot(transType: number, recId: number, byUserName?: string) {
    return authenticatedAxiosInstance.post(
      `${routes.ACTIVE_VEHICLE_GOT}`,
      { transType, recId, byUserName },
      {
        headers: authHeader(),
      }
    );
  }

  async getBalanceHistory(CompId?: string, signal?: AbortSignal) {
    const result = await authenticatedAxiosInstance.get(routes.BALANCE_HISTORY, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
    return result;
  }

  async getBillingPaymentLog(CompId?: string, signal?: AbortSignal) {
    const result = await authenticatedAxiosInstance.get(routes.BILLING_PAYMENT_LOG, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
    return result;
  }

  getBillingPaymentLogEntry(id: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.BILLING_PAYMENT_LOG}/${id}`, {
      headers: authHeader(),
      signal,
    });
  }

  getBillingPaymentLogDetail(BillingLogId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.BILLING_PAYMENT_LOG_DETAIL, {
      headers: authHeader(),
      params: { BillingLogId },
      signal,
    });
  }

  getAssignableLenders(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.ASSIGNABLE_LENDERS, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  assignLender(recIds: number[], outsideLenderName: string) {
    return authenticatedAxiosInstance.post(
      routes.ASSIGN_LENDER,
      { recIds, outsideLenderName },
      { headers: authHeader() }
    );
  }

  async getFundingLogs(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.FUNDING_LOGS}`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  getFundingLog(id?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.FUNDING_LOGS}/${id}`, {
      headers: authHeader(),
      signal,
    });
  }

  async getPaidOffVehicles(CompId?: string, searchText?: string, signal?: AbortSignal) {
    const result = await authenticatedAxiosInstance.get(routes.PAID_OFF_VEHICLES, {
      headers: authHeader(),
      params: { CompId, searchText },
      signal,
    });
    return result;
  }

  getPayoffRequests(CompId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.PAYOFF_REQUESTS, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  async getPendingFunding(CompId?: string, searchText?: string, signal?: AbortSignal) {
    const result = await authenticatedAxiosInstance.get(routes.PENDING_FUNDING, {
      headers: authHeader(),
      params: { CompId, searchText },
      signal,
    });
    return result;
  }

  payoffVehicles(
    vehicleList: any[],
    payoffDate: string,
    byUserName: string,
    compId: string,
    payWhat: string
  ) {
    return authenticatedAxiosInstance.post(
      routes.PAYOFF_VEHICLES,
      { vehicleList, payoffDate, byUserName, compId, payWhat },
      { headers: authHeader() }
    );
  }

  payoffLoans(
    loanList: any[],
    payoffDate: string,
    byUserName: string,
    compId: string,
    payWhat: string
  ) {
    return authenticatedAxiosInstance.post(
      routes.PAYOFF_LOANS,
      { loanList, payoffDate, byUserName, compId, payWhat },
      { headers: authHeader() }
    );
  }

  importFundingRequest(formData: FormData) {
    return authenticatedAxiosInstance.post(`${API_URL}/import-funding-request`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  generateGuaranteeOfTitleDocument(vehicleRecId: number, byUserName: string) {
    return authenticatedAxiosInstance.post(
      `${API_URL}/generate-document`,
      {
        vehicleRecId,
        documentType: 'guarantee-of-title',
        byUserName,
      },
      { headers: authHeader() }
    );
  }

  getApprovedVehicles(CompId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.APPROVED_VEHICLES, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  getActiveLoans(CompId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.ACTIVE_LOANS, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  getApprovedLoans(CompId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.APPROVED_LOANS, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  fundingVehicles(funddate: string, fundingList: any[], compId: string, byUserName?: string) {
    return authenticatedAxiosInstance.post(
      routes.FUNDING_VEHICLES,
      { funddate, fundingList, compId, byUserName },
      { headers: authHeader() }
    );
  }

  cancelVehicles(cancelList: any[]) {
    return authenticatedAxiosInstance.post(
      routes.CANCEL_VEHICLES,
      { cancelList },
      { headers: authHeader() }
    );
  }

  createVehicle(data: any) {
    return authenticatedAxiosInstance.post(routes.VEHICLE, data, { headers: authHeader() });
  }

  /** A PAID VinAudit lookup, staff only. The API registers this route as POST only. */
  decodeVin(vin: string, compId: string | number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.post(
      `${routes.DECODE_VIN}`,
      { VIN: vin, compId },
      { headers: authHeader(), signal }
    );
  }

  getFundingDetails(fundRecId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.FUNDING_DETAILS}`, {
      headers: authHeader(),
      params: { FundRecID: fundRecId },
      signal,
    });
  }

  /**
   * Get API endpoint documentation for debugging purposes
   * @returns {Promise} - API response with endpoint documentation
   */
  getApiDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${API_URL}/docs`, { headers: authHeader(), signal });
  }

  /**
   * Get SQL procedure documentation for debugging purposes
   * @returns {Promise} - API response with SQL procedure documentation
   */
  getSqlDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${API_URL}/sql-docs`, { headers: authHeader(), signal });
  }
}

export default new FloorplansService();
